import {
    Component,
    ElementRef,
    forwardRef,
    inject,
    Injectable,
    Injector,
    input,
    model,
    OnInit,
    Renderer2,
    viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';


@Injectable({
    providedIn: 'root',
})
export class RadioControlRegistry {

    private accessors: [NgControl, RadioComponent][] = [];

    add(control: NgControl, accessor: RadioComponent) {
        this.accessors.push([control, accessor]);
    }

    remove(accessor: RadioComponent) {
        for (let i = this.accessors.length - 1; i >= 0; --i) {
            if (this.accessors[i][1] === accessor) {
                this.accessors.splice(i, 1);
                return;
            }
        }
    }

    select(accessor: RadioComponent) {
        this.accessors.forEach((c) => {
            if (this.isSameGroup(c, accessor) && c[1] !== accessor) {
                c[1].writeValue(accessor.value());
            }
        });
    }

    isSameGroup(controlPair: [NgControl, RadioComponent], accessor: RadioComponent) {
        if (!controlPair[0].control) {
            return false;
        }
        return (controlPair[0] as any)._parent === (accessor.control as any)._parent && controlPair[1].name() === accessor.name();
    }
}


let id = 0;

@Component({
    selector: 'ui-radio',
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioComponent), multi: true },
    ],
    styles: [
        `
        :host {
            position: relative;
            display: inline-flex;
            align-items: baseline;
            column-gap: 0.5rem;
        }
        input {
            cursor: pointer;
        }
        input:disabled,
        input:disabled + label {
            cursor: not-allowed;
        }
        label {
            display: inline-block;
            user-select: none;
            cursor: pointer;
            margin: 0;
        }
        `,
    ],
    template: `
        <input
            type="radio"
            #inputEl
            [attr.id]="id"
            [value]="value()"
            (change)="change()"
            (blur)="onTouched()"
            [attr.disabled]="inactive() ? '' : null">
        <label [attr.for]="id">
            <ng-content></ng-content>
        </label>
    `,
})
export class RadioComponent implements ControlValueAccessor, OnInit {

    inputEl = viewChild.required<ElementRef>('inputEl');

    value = input.required<string>();

    name = model<string | number>();

    inactive = input(false);

    onChange = (_: string) => {};

    onTouched = () => {};

    id = `uiRb${id++}`;

    control: NgControl;

    private renderer = inject(Renderer2);

    private injector = inject(Injector);

    private registry = inject(RadioControlRegistry);

    ngOnInit() {
        this.control = this.injector.get(NgControl);
        if (!this.name() && this.control.name) {
            this.name.set(this.control.name);
        }
        if (!this.name()) {
            throw Error(`radio name not set`);
        }
        this.registry.add(this.control, this);
    }

    change() {
        this.onChange(this.value());
        this.registry.select(this);
        this.onTouched();
    }

    writeValue(value: string) {
        this.renderer.setProperty(this.inputEl().nativeElement, 'checked', this.value() === value);
    }

    registerOnChange(fn: (_: string) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.renderer.setProperty(this.inputEl().nativeElement, 'disabled', isDisabled);
    }

    ngOnDestroy() {
        this.registry.remove(this);
    }
}
